import React, { useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { keyframes } from '@emotion/react'

// Durations and easings match the material-ui Drawer this replaces
const ENTER_MS = 225
const EXIT_MS = 195
const ENTER_EASING = 'cubic-bezier(0, 0, 0.2, 1)'
const EXIT_EASING = 'cubic-bezier(0.4, 0, 0.6, 1)'

const slideIn = keyframes({
  from: { transform: 'translateX(100%)' },
  to: { transform: 'translateX(0)' }
})

const slideOut = keyframes({
  from: { transform: 'translateX(0)' },
  to: { transform: 'translateX(100%)' }
})

const fadeIn = keyframes({
  from: { opacity: 0 },
  to: { opacity: 1 }
})

const fadeOut = keyframes({
  from: { opacity: 1 },
  to: { opacity: 0 }
})

// Right-anchored panel; showModal() puts it in the top layer with a native backdrop
const Panel = styled('dialog')({
  margin: '0 0 0 auto',
  padding: 0,
  border: 'none',
  height: '100%',
  maxHeight: '100%',
  maxWidth: '100%',
  overflowY: 'auto',
  backgroundColor: 'white',
  boxShadow:
    '0 8px 10px -5px rgba(0, 0, 0, 0.2), 0 16px 24px 2px rgba(0, 0, 0, 0.14), 0 6px 30px 5px rgba(0, 0, 0, 0.12)',
  '&::backdrop': {
    backgroundColor: 'rgba(0, 0, 0, 0.5)'
  },
  '&[open]': {
    animation: `${slideIn} ${ENTER_MS}ms ${ENTER_EASING}`
  },
  '&[open]::backdrop': {
    animation: `${fadeIn} ${ENTER_MS}ms ${ENTER_EASING}`
  },
  // close() is deferred until the exit animation finishes; see Drawer below
  '&[data-closing]': {
    animation: `${slideOut} ${EXIT_MS}ms ${EXIT_EASING} forwards`
  },
  '&[data-closing]::backdrop': {
    animation: `${fadeOut} ${EXIT_MS}ms ${EXIT_EASING} forwards`
  },
  '@media (prefers-reduced-motion: reduce)': {
    '&[open], &[open]::backdrop, &[data-closing], &[data-closing]::backdrop': {
      animation: 'none'
    }
  }
})

// Matches the media query that disables the animations, so closing doesn't wait on one that won't play
const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

interface Props {
  open: boolean
  onClose: () => void
}

export const Drawer: React.FC<React.PropsWithChildren<Props>> = ({ open, onClose, children }) => {
  const dialogRef = useRef<HTMLDialogElement>(null)
  // Keeps content rendered while the exit animation plays
  const [closing, setClosing] = useState(false)

  // Callers pass a new onClose each render; a ref lets the cancel listener stay attached once
  const onCloseRef = useRef(onClose)
  useEffect(() => {
    onCloseRef.current = onClose
  })

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return undefined

    if (open) {
      dialog.removeAttribute('data-closing')
      setClosing(false)
      if (!dialog.open) dialog.showModal()
      document.body.style.overflow = 'hidden'
      return () => {
        document.body.style.overflow = ''
      }
    }

    if (!dialog.open) return undefined

    if (prefersReducedMotion()) {
      dialog.close()
      return undefined
    }

    // A timer rather than animationend, which never fires when animations are disabled
    dialog.setAttribute('data-closing', '')
    setClosing(true)
    const timer = window.setTimeout(() => {
      dialog.close()
      dialog.removeAttribute('data-closing')
      setClosing(false)
    }, EXIT_MS)
    return () => window.clearTimeout(timer)
  }, [open])

  // Esc fires `cancel`; route it through onClose so `open` stays the source of truth
  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return undefined

    const handleCancel = (event: Event) => {
      event.preventDefault()
      onCloseRef.current()
    }
    dialog.addEventListener('cancel', handleCancel)
    return () => dialog.removeEventListener('cancel', handleCancel)
  }, [])

  // A click whose target is the dialog itself landed on the backdrop, outside the panel content
  const handleClick = (event: React.MouseEvent<HTMLDialogElement>) => {
    if (event.target === event.currentTarget) onClose()
  }

  return (
    <Panel ref={dialogRef} onClick={handleClick}>
      {(open || closing) && children}
    </Panel>
  )
}
